import React, { useEffect, useState } from 'react'
import { useAppSelector } from 'store/hooks'
import { useThunkDispatch } from 'models/thunk'
import useLoader from 'hooks/Shared/useLoader'

import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import Divider from '@material-ui/core/Divider'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import Activity from 'components/Shared/Activity/Activity'
import Button from 'components/Shared/Button/Button'
import Form from 'components/FormComponents/IOComponents/IOCustomForm/IOCustomForm'
import TextField from 'components/FormComponents/IOComponents/TextField/TextField'
import NumericField from 'components/FormComponents/IOComponents/NumericField/NumericField'
import SearchField from 'components/FormComponents/IOComponents/SearchField'
import AutocompleteField from 'components/FormComponents/IOComponents/AutocompleteField/AutoCompleteField'

import SearchSatCodeWizardContainer from 'containers/Shared/SearchSatCodeWizardContainer/SearchSatCodeWizardContainer'

import useProductsList, { useAddProduct } from 'hooks/queries/products'
import useCategoriesList from 'hooks/queries/categories'

import { searchInCatalogue } from 'actions/catalogues'
import {
  selectCatalogue,
  selectCategoriesList,
  selectProductsListIds,
} from 'lib/helpers/selectors'

import Search from '@material-ui/icons/Search'
import './style.scss'

import { ProductForm } from 'models/product'
import { CatalogueEntry } from 'models/catalogues'

interface Props {
  workspaceId: number
  onRedirect: (action: string) => void
}

const AddProductContainer: React.FC<Props> = ({
  workspaceId,
  onRedirect,
  ...props
}) => {
  const thunkDispatch = useThunkDispatch()
  const loader = useLoader()

  const impuestoCat = useAppSelector(selectCatalogue('objImp'))
  const unidadesCat = useAppSelector(selectCatalogue('unidades'))
  const categories = useAppSelector(selectCategoriesList)
  const productsId = useAppSelector(selectProductsListIds())

  const [valid, setValid] = useState(false)
  const [openWizard, setOpenWizard] = useState(false)
  const [item, setItem] = useState<CatalogueEntry | null>(null)

  const categoriesListQuery = useCategoriesList(workspaceId, {
    onError: () => onRedirect('error'),
  })
  const productsListQuery = useProductsList(workspaceId, {
    onError: () => onRedirect('error'),
  })

  const addMutation = useAddProduct(workspaceId, {
    onSuccess: () => onRedirect('success'),
  })

  useEffect(() => {
    productsListQuery.isLoading || categoriesListQuery.isLoading
      ? loader.primary('Cargando información del producto')
      : loader.close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsListQuery.isLoading, categoriesListQuery.isLoading])

  const renderCatalogueOption = (o: CatalogueEntry) => (
    <span className="catalogue-option">{`${o.value} - ${o.label}`}</span>
  )

  return (
    <>
      <Card elevation={1} className="container-add-product">
        <Form
          onSubmit={async (form: ProductForm) => addMutation.mutate(form)}
          // onSubmit={async (form: ProductForm) => console.log(form)}
          onValidity={(v: boolean) => setValid(v)}>
          <div className="description">
            <p>
              Completa la siguiente información para agregar un nuevo producto
              al catálogo
            </p>
            {(productsListQuery.isFetching ||
              categoriesListQuery.isFetching) && <Activity size={22} local />}
          </div>
          <Divider />
          <div className="content">
            <Grid container spacing={4}>
              <Grid item xs={4}>
                <TextField
                  fieldType="alphanumeric"
                  name="internalId"
                  label="Identificador"
                  validate={(v: number) => ({
                    valid: productsId.indexOf(v) === -1,
                    message: 'Este identificador ya existe en el catálogo.',
                  })}
                  placeholder="Serie, Modelo, No. de Lote, etc."
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField name="name" label="Nombre" fullWidth required />
              </Grid>
              <Grid item xs={4}>
                <NumericField
                  name="price"
                  label="Precio"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  format="money"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <SearchField
                  name="itemSat"
                  label="Clave SAT"
                  onSearch={(v: string) =>
                    thunkDispatch(searchInCatalogue('items', v))
                  }
                  value={item}
                  getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                  renderOption={(o: any) => renderCatalogueOption(o)}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setOpenWizard(true)}
                        size="small">
                        <Search />
                      </IconButton>
                    ),
                  }}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <AutocompleteField
                  name="unitSat"
                  label="Unidad o medida"
                  catalogue={unidadesCat}
                  getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                  renderOption={(o: any) => renderCatalogueOption(o)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <AutocompleteField
                  name="tax"
                  label="Objeto de Impuesto"
                  catalogue={impuestoCat}
                  getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                  renderOption={(o: any) => renderCatalogueOption(o)}
                  fullWidth
                  required // ToDo: Revisar si es requerido
                />
              </Grid>
              <Grid item xs={4}>
                <AutocompleteField
                  name="category"
                  label="Categoría"
                  catalogue={categories}
                  getOptionLabel={(o: any) => `${o.name}`}
                  fullWidth
                />
              </Grid>
              <Grid item xs={8} />
              <Grid item xs={8}>
                <p className="form-label">Descripción</p>
                <TextField
                  variant="outlined"
                  name="description"
                  inputProps={{ maxLength: 500 }}
                  multiline
                  minRows={4}
                  maxRows={4}
                  withCounter
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
          </div>
          <Divider />
          <div className="actions">
            <Button
              id="addProductBtn"
              variant="contained"
              type="submit"
              color="secondary"
              activity={addMutation.isLoading}
              disabled={!valid}>
              Agregar
            </Button>
          </div>
        </Form>
      </Card>
      <SearchSatCodeWizardContainer
        open={openWizard}
        onSelect={v => setItem(v)}
        onCancel={() => setOpenWizard(false)}
      />
    </>
  )
}

export default AddProductContainer
